import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./revenue.css";
import { getRevenueCategory, updateCategoryCycle, errorMessage } from "./revenueApi";
import { Avatar, Kpi, Loading, RangeBar, RevenueChart, UserCell } from "./parts";
import { GiftsTab } from "./RevenueSection";
import { CYCLE_STATUS, compact, dateOnly, fmt } from "./format";
import RunControl, { RunLog } from "./RunControl";

/// One target category: its status and clock (Start / Pause / Resume / Stop),
/// the current cycle (collected, held, time left), what it earned in the
/// range, its top users, its cycle + status history and gift log. The admin
/// sets the goal and the cycle length here.
export default function RevenueCategoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [range, setRange] = useState({ from: "", to: "", preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [goal, setGoal] = useState("");
  const [months, setMonths] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return getRevenueCategory(id, { from: range.from, to: range.to })
      .then((r) => {
        setData(r);
        setGoal(String(r.category.cycle_goal_coins));
        setMonths(String(r.category.cycle_months));
      })
      .catch((e) => setError(errorMessage(e, "Could not load category")))
      .finally(() => setLoading(false));
  }, [id, range.from, range.to]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaveMsg(null);
    const g = parseInt(goal, 10);
    const m = parseInt(months, 10);
    if (!(g > 0) || !(m >= 1 && m <= 24)) {
      setSaveMsg({ ok: false, text: "Goal must be above 0 and the cycle 1–24 months." });
      return;
    }
    setSaving(true);
    try {
      await updateCategoryCycle(id, { username: data.category.name, cycle_goal_coins: g, cycle_months: m });
      setSaveMsg({ ok: true, text: "Saved. A new goal applies to the current cycle; a new length starts with the next cycle (next Start or next month)." });
      await load();
    } catch (err) {
      setSaveMsg({ ok: false, text: errorMessage(err, "Could not save") });
    } finally {
      setSaving(false);
    }
  };

  const c = data?.category;
  const s = data?.summary;

  return (
    <div className="rv-wrap">
      <Link to="/dashboard/home" className="rv-back">
        ← Back to revenue
      </Link>
      {error && <div className="rv-error my-3">{error}</div>}
      {loading && !data ? (
        <Loading />
      ) : c ? (
        <>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 my-3">
            <div className="d-flex align-items-center gap-3">
              <Avatar img={c.img} name={c.name} size="lg" />
              <div>
                <h2 className="rv-title">{c.name}</h2>
                <div className="d-flex gap-2 mt-1">
                  <span className="rv-badge rv-badge-gold">{fmt(c.coins)} coins</span>
                  <Link to={`/dashboard/wallet?type=category&id=${c.id}`} className="rv-badge rv-badge-green text-decoration-none">
                    Adjust balance →
                  </Link>
                </div>
              </div>
            </div>
            <RangeBar value={range} onChange={setRange} />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-8">
              <RunControl categoryId={c.id} run={data.run} onChanged={load} showDefaultToggle />
            </div>
            <div className="col-lg-4">
              <form className="rv-panel h-100" onSubmit={save}>
                <div className="rv-panel-title">Goal settings</div>
                <label className="rv-label">Goal (coins, after the store cut)</label>
                <input className="rv-input mb-3" type="number" min="1" value={goal} onChange={(e) => setGoal(e.target.value)} />
                <label className="rv-label">Cycle length (months)</label>
                <input className="rv-input mb-3" type="number" min="1" max="24" value={months} onChange={(e) => setMonths(e.target.value)} />
                <button className="rv-btn violet w-100" disabled={saving}>
                  {saving ? "Saving…" : "Save"}
                </button>
                {saveMsg && <div className={`mt-3 ${saveMsg.ok ? "rv-success" : "rv-error"}`}>{saveMsg.text}</div>}
              </form>
            </div>
          </div>

          <div className="rv-kpis mb-3">
            <Kpi tone="green" label="Earned" value={compact(s.category.total)} title={fmt(s.category.total)} foot={<>Direct <b>{fmt(s.category.direct)}</b> · released <b>{fmt(s.category.released)}</b></>} />
            <Kpi tone="grey" label="Pending created" value={compact(s.pending.created)} foot={<>Went to app <b>{fmt(s.pending.forfeited)}</b></>} />
            <Kpi tone="gold" label="Coins generated" value={compact(s.net)} foot={<>Counts toward the goal · <b>{fmt(s.gifts_count)}</b> gifts</>} />
            <Kpi tone="pink" label="Users' diamonds" value={compact(s.host.total)} foot={<><b>{fmt(s.counts.receivers)}</b> users · <b>{fmt(s.counts.senders)}</b> senders</>} />
            <Kpi tone="violet" label="App from this category" value={compact(s.app.total)} />
          </div>

          <div className="rv-panel mb-3">
            <div className="rv-panel-title">Trend</div>
            <RevenueChart points={data.timeseries.points} keys={["category", "pending", "app", "host"]} />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <div className="rv-panel h-100">
                <div className="rv-panel-title">Top users</div>
                {data.top_users?.data?.length ? (
                  data.top_users.data.map((r) => (
                    <div key={r.user.id} className="rv-stat-row" style={{ cursor: "pointer" }} onClick={() => navigate(`/dashboard/revenue/users/${r.user.id}`)}>
                      <span className="d-flex align-items-center gap-2">
                        <span className="text-grey">{r.rank}</span>
                        <UserCell user={r.user} sub={`${fmt(r.gifts_count)} gifts · ${fmt(r.net)} coins`} />
                      </span>
                      <span>{fmt(r.category_generated)}</span>
                    </div>
                  ))
                ) : (
                  <div className="rv-empty">No gifts in this range.</div>
                )}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="rv-panel h-100">
                <div className="rv-panel-title">Cycle history</div>
                {data.cycles?.length ? (
                  <div className="rv-table-wrap">
                    <table className="rv-table" style={{ minWidth: 480 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Period</th>
                          <th className="rv-num">Achieved</th>
                          <th className="rv-num">Released</th>
                          <th className="rv-num">To app</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.cycles.map((x) => (
                          <tr key={x.id}>
                            <td>{x.cycle_no}</td>
                            <td className="small text-grey">
                              {dateOnly(x.starts_at)} → {dateOnly(x.ends_at)}
                            </td>
                            <td className="rv-num">
                              {compact(x.achieved_coins)} / {compact(x.goal_coins)}
                            </td>
                            <td className="rv-num">{fmt(x.released_coins)}</td>
                            <td className="rv-num">{fmt(x.forfeited_coins)}</td>
                            <td>
                              <span className={`rv-badge ${CYCLE_STATUS[x.status]?.cls}`}>{CYCLE_STATUS[x.status]?.label}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rv-empty">No cycles yet.</div>
                )}
              </div>
            </div>
          </div>

          <div className="rv-panel mb-3">
            <div className="rv-panel-title">Status history</div>
            <RunLog log={data.run?.log || []} />
          </div>

          <div className="rv-panel">
            <div className="rv-panel-title">Gift log</div>
            <GiftsTab range={range} categoryId={c.id} />
          </div>
        </>
      ) : null}
    </div>
  );
}
